import { api, publicApi } from './axios';

/** @typedef {import('../model/battle').BattleRequest} BattleRequest */
/** @typedef {import('../model/battle').BattleResponse} BattleResponse */
/** @typedef {import('../model/battle').BattleListResponse} BattleListResponse */
/** @typedef {import('../model/battle').RoundRequest} RoundRequest */
/** @typedef {import('../model/battle').RoundResponse} RoundResponse */
/** @typedef {import('../model/battle').TrackRequest} TrackRequest */
/** @typedef {import('../model/battle').TrackResponse} TrackResponse */
/** @typedef {import('../model/battle').UpdateBattleStatusRequest} UpdateBattleStatusRequest */
/** @typedef {import('../model/battle').BattlePairRequest} BattlePairRequest */
/** @typedef {import('../model/battle').BattlePairResponse} BattlePairResponse */
/** @typedef {import('../model/battle').PairDrawRequest} PairDrawRequest */
/** @typedef {import('../model/battle').PairDrawResponse} PairDrawResponse */

const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * API для работы с баттлами.
 *
 * Статусы: PLANNED | ACTIVE | FINISHED | CANCELLED
 * Типы:   SINGLE | TEAM
 * Видимость: PUBLIC | PRIVATE | INVITE_ONLY
 *
 * V2 API: Публичный доступ с возрастной фильтрацией
 * @see api_battles_v2_reference.md
 */
export const battlesApi = {
    // ==================== V2 API - Публичный доступ ====================

    /**
     * [V2] Получить публичный список баттлов с пагинацией и фильтрами.
     * Не требует авторизации. Автоматическая фильтрация по возрастным ограничениям.
     * @param {Object} params — page, size, sortBy, direction, status, type, format, search
     * @returns {Promise<{content: BattleListResponse[], pageable, totalElements, totalPages, last, first, numberOfElements, number, sort, empty}>}
     */
    listPublic: async (params = {}) => {
        try {
            const { data } = await publicApi.get('/battles/public', { params });
            return data;
        } catch (error) {
            if (error.response?.status === 404) {
                console.error('[battlesApi.listPublic] Public battles endpoint not found');
                throw new Error('Публичный список баттлов недоступен');
            }
            console.error('[battlesApi.listPublic] Failed to fetch public battles:', error.message);
            throw error;
        }
    },

    /**
     * [V2] Проверить доступ пользователя к баттлу.
     * Требуется авторизация для определения возраста пользователя.
     * @param {string} id — UUID баттла
     * @returns {Promise<{battleId: string, hasAccess: boolean, userAge: number, ageRestriction: number, reason: string}>}
     */
    checkAccess: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${id}/access`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.checkAccess] Failed to check access for battle ${id}:`, error.message);
            throw error;
        }
    },

    // ==================== V1 API - Управление баттлами ====================

    /**
     * Получить список баттлов с пагинацией и фильтрами.
     * Требуется авторизация.
     * @param {Object} params — page, size, sortBy, direction, status, type, format, search
     * @returns {Promise<{content: BattleListResponse[], pageable, totalElements, totalPages}>}
     */
    list: async (params = {}) => {
        try {
            const { data } = await api.get('/battles', { params });
            return data;
        } catch (error) {
            console.error('[battlesApi.list] Failed to fetch battles:', error.message);
            throw error;
        }
    },

    /**
     * Получить данные баттла по ID.
     * Требуется авторизация. Проверяется возрастной доступ.
     * @param {string} id — UUID баттла
     * @returns {Promise<BattleResponse & {ageRestriction: number, organizers: BattlePersonResponse[], participants: BattlePersonResponse[], judges: BattlePersonResponse[], tags: Array<{id: string, name: string, color: string}>, regulation: RegulationResponse|null, forumCategoryId: string|null, forumStartPostId: string|null, liveChatId: string|null, participantsChatId: string|null}>}
     * @throws {Error} 403 — Доступ запрещён (возрастное ограничение)
     * @throws {Error} 404 — Баттл не найден
     */
    get: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${id}`);
            return data;
        } catch (error) {
            if (error.response?.status === 403) {
                console.error(`[battlesApi.get] Access denied for battle ${id}: age restriction`);
                throw new Error('Доступ к баттлу запрещён из-за возрастного ограничения');
            }
            if (error.response?.status === 404) {
                console.error(`[battlesApi.get] Battle ${id} not found`);
                throw new Error('Баттл не найден');
            }
            console.error(`[battlesApi.get] Failed to fetch battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Создать новый баттл.
     * @param {Object} payload — BattleRequest
     * @returns {Promise<BattleResponse>}
     */
    create: async (payload) => {
        if (!payload) throw new Error('Battle payload is required');
        try {
            const { data } = await api.post('/battles', payload);
            return data;
        } catch (error) {
            console.error('[battlesApi.create] Failed to create battle:', error.message);
            throw error;
        }
    },

    /**
     * Обновить данные баттла.
     * @param {string} id — UUID баттла
     * @param {Object} payload — BattleRequest
     * @returns {Promise<BattleResponse>}
     */
    update: async (id, payload) => {
        validateId(id, 'Battle ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/battles/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.update] Failed to update battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить баттл.
     * @param {string} id — UUID баттла
     * @returns {Promise<void>}
     */
    remove: async (id) => {
        validateId(id, 'Battle ID');
        try {
            await api.delete(`/battles/${id}`);
        } catch (error) {
            console.error(`[battlesApi.remove] Failed to delete battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Изменить статус баттла (ADMIN).
     * @param {string} id — UUID баттла
     * @param {string} status — PLANNED | ACTIVE | FINISHED | CANCELLED
     * @returns {Promise<BattleResponse>}
     */
    updateStatus: async (id, status) => {
        validateId(id, 'Battle ID');
        if (!status) throw new Error('Status is required');
        try {
            const { data } = await api.patch(`/battles/${id}/status`, { status });
            return data;
        } catch (error) {
            console.error(`[battlesApi.updateStatus] Failed to update battle ${id} status:`, error.message);
            throw error;
        }
    },

    /**
     * Зарегистрировать текущего пользователя на баттл.
     * @param {string} id — UUID баттла
     * @returns {Promise<BattleResponse>}
     */
    register: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.post(`/battles/${id}/register`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.register] Failed to register for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Отменить регистрацию текущего пользователя с баттла.
     * @param {string} id — UUID баттла
     * @returns {Promise<BattleResponse>}
     */
    unregister: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.delete(`/battles/${id}/unregister`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.unregister] Failed to unregister from battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить пагинированный список участников баттла.
     * @param {string} id — UUID баттла
     * @param {Object} [params] — page, size, sortBy, direction
     * @returns {Promise<{content: BattlePersonResponse[], totalElements, totalPages}>}
     * @throws {Error} 404 — Баттл не найден
     */
    getParticipants: async (id, params = {}) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${id}/participants`, { params });
            return data;
        } catch (error) {
            if (error.response?.status === 404) {
                console.error(`[battlesApi.getParticipants] Battle ${id} not found`);
                throw new Error('Баттл не найден');
            }
            console.error(`[battlesApi.getParticipants] Failed to fetch participants for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить раунды баттла.
     * @param {string} id — UUID баттла
     * @returns {Promise<RoundResponse[]>}
     */
    getRounds: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${id}/rounds`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getRounds] Failed to fetch rounds for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Создать раунд для баттла.
     * @param {string} id — UUID баттла
     * @param {Object} payload — RoundRequest
     * @param {string} payload.title - Название раунда
     * @param {string} [payload.description] - Описание
     * @param {string} [payload.task] - Задание для раунда
     * @param {RoundType} payload.type - Тип раунда
     * @param {RoundStatus} [payload.status] - Статус
     * @param {number} payload.number - Номер раунда
     * @param {string} payload.startTime - Дата начала
     * @param {string} payload.endTime - Дата окончания
     * @param {boolean} [payload.votingEnabled] - Голосование разрешено
     * @param {string} [payload.votingEndTime] - Окончание голосования
     * @param {string} [payload.trackUploadDeadline] - Дедлайн загрузки треков
     * @param {VotingSystem} [payload.votingSystem] - Система голосования
     * @param {CriteriaPreset} [payload.criteriaPreset] - Пресет критериев
     * @param {Array} [payload.limitations] - Ограничения раунда
     * @param {string[]} [payload.participants] - UUID участников
     * @returns {Promise<RoundResponse>}
     */
    createRound: async (id, payload) => {
        validateId(id, 'Battle ID');
        if (!payload) throw new Error('Round payload is required');
        try {
            const { data } = await api.post(`/battles/${id}/rounds`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.createRound] Failed to create round for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить раунд баттла.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {Object} payload — RoundRequest
     * @param {string} [payload.title] - Название раунда
     * @param {string} [payload.description] - Описание
     * @param {string} [payload.task] - Задание для раунда
     * @param {RoundType} [payload.type] - Тип раунда
     * @param {RoundStatus} [payload.status] - Статус
     * @param {number} [payload.number] - Номер раунда
     * @param {string} [payload.startTime] - Дата начала
     * @param {string} [payload.endTime] - Дата окончания
     * @param {boolean} [payload.votingEnabled] - Голосование разрешено
     * @param {string} [payload.votingEndTime] - Окончание голосования
     * @param {string} [payload.trackUploadDeadline] - Дедлайн загрузки треков
     * @param {VotingSystem} [payload.votingSystem] - Система голосования
     * @param {CriteriaPreset} [payload.criteriaPreset] - Пресет критериев
     * @param {Array} [payload.limitations] - Ограничения раунда
     * @returns {Promise<RoundResponse>}
     */
    updateRound: async (id, roundId, payload) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Round payload is required');
        try {
            const { data } = await api.put(`/battles/${id}/rounds/${roundId}`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.updateRound] Failed to update round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Изменить статус раунда.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} status — PREPARATION | RUNNING | ENDED
     * @returns {Promise<RoundResponse>}
     */
    updateRoundStatus: async (id, roundId, status) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!status) throw new Error('Status is required');
        try {
            const { data } = await api.patch(`/battles/${id}/rounds/${roundId}/status`, null, {
                params: { status },
            });
            return data;
        } catch (error) {
            console.error(`[battlesApi.updateRoundStatus] Failed to update round ${roundId} status:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить раунд баттла.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @returns {Promise<void>}
     */
    deleteRound: async (id, roundId) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            await api.delete(`/battles/${id}/rounds/${roundId}`);
        } catch (error) {
            console.error(`[battlesApi.deleteRound] Failed to delete round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавить участников в раунд.
     * Endpoint: POST /battles/{id}/rounds/{roundId}/participants
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {Array<string>} participantIds — массив UUID участников (battle_person_id)
     * @returns {Promise<RoundResponse>}
     */
    addRoundParticipants: async (id, roundId, participantIds) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!Array.isArray(participantIds) || participantIds.length === 0) {
            throw new Error('participantIds must be a non-empty array');
        }
        try {
            const { data } = await api.post(
                `/battles/${id}/rounds/${roundId}/participants`,
                { participantIds }
            );
            return data;
        } catch (error) {
            console.error(`[battlesApi.addRoundParticipants] Failed to add participants to round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить участника из раунда.
     * Endpoint: DELETE /battles/{id}/rounds/{roundId}/participants/{participantId}
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} participantId — UUID участника для удаления (battle_person_id)
     * @returns {Promise<RoundResponse>}
     */
    removeRoundParticipant: async (id, roundId, participantId) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(participantId, 'Participant ID');
        try {
            const { data } = await api.delete(
                `/battles/${id}/rounds/${roundId}/participants/${participantId}`
            );
            return data;
        } catch (error) {
            console.error(`[battlesApi.removeRoundParticipant] Failed to remove participant ${participantId} from round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить работы (треки) раунда.
     * Endpoint: GET /battles/{id}/rounds/{roundId}/tracks
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @returns {Promise<import('../model/battle').WorkResponse[]>} Список работ раунда
     */
    getRoundTracks: async (id, roundId) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.get(`/battles/${id}/rounds/${roundId}/tracks`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getRoundTracks] Failed to fetch tracks for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить все треки с пагинацией и фильтрами.
     * Endpoint: GET /api/tracks
     * @param {Object} [params] - Параметры запроса
     * @param {number} [params.page] - Номер страницы (0 by default)
     * @param {number} [params.size] - Размер страницы (20 by default, max 100)
     * @param {string} [params.battleId] - Фильтр по ID баттла
     * @param {string} [params.roundId] - Фильтр по ID раунда
     * @param {string} [params.authorId] - Фильтр по ID автора
     * @param {string} [params.format] - Фильтр по формату (mp3, wav, ogg, flac, aac, m4a)
     * @param {string} [params.sortBy] - Сортировка: uploadedAt, duration, fileName (uploadedAt by default)
     * @param {string} [params.sortDir] - Направление: ASC, DESC (DESC by default)
     * @returns {Promise<{tracks: Array, page: number, size: number, totalElements: number, totalPages: number, first: boolean, last: boolean}>}
     */
    getTracks: async (params = {}) => {
        try {
            const { data } = await api.get('/tracks', { params });
            return data;
        } catch (error) {
            console.error('[battlesApi.getTracks] Failed to fetch tracks:', error.message);
            throw error;
        }
    },

    /**
     * Получить трек по ID.
     * Endpoint: GET /api/tracks/{trackId}
     * @param {string} trackId - UUID трека
     * @returns {Promise<TrackInfoResponse>}
     */
    getTrackById: async (trackId) => {
        validateId(trackId, 'Track ID');
        try {
            const { data } = await api.get(`/tracks/${trackId}`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getTrackById] Failed to fetch track ${trackId}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить трек.
     * Endpoint: PUT /api/battles/{battleId}/rounds/{roundId}/tracks/{trackId}
     * @param {string} battleId - UUID баттла
     * @param {string} roundId - UUID раунда
     * @param {string} trackId - UUID трека
     * @param {TrackRequest} payload - { authorId, title, description, text?, duration?, format, mediaUrl?, status? }
     * @returns {Promise<TrackResponse>}
     */
    updateTrack: async (battleId, roundId, trackId, payload) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(trackId, 'Track ID');
        if (!payload) throw new Error('Payload is required');
        try {
            const { data } = await api.put(
                `/battles/${battleId}/rounds/${roundId}/tracks/${trackId}`,
                payload
            );
            return data;
        } catch (error) {
            console.error(`[battlesApi.updateTrack] Failed to update track ${trackId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить трек.
     * Endpoint: DELETE /api/battles/{battleId}/rounds/{roundId}/tracks/{trackId}
     * @param {string} battleId - UUID баттла
     * @param {string} roundId - UUID раунда
     * @param {string} trackId - UUID трека
     * @returns {Promise<void>}
     */
    deleteTrack: async (battleId, roundId, trackId) => {
        validateId(battleId, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(trackId, 'Track ID');
        try {
            await api.delete(`/battles/${battleId}/rounds/${roundId}/tracks/${trackId}`);
        } catch (error) {
            console.error(`[battlesApi.deleteTrack] Failed to delete track ${trackId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить правила баттла.
     * @param {string} id — UUID баттла
     * @returns {Promise<import('../model/battle').RuleResponse[]>}
     */
    getRules: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${id}/rules`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getRules] Failed to fetch rules for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить ограничения баттла.
     * @param {string} id — UUID баттла
     * @returns {Promise<import('../model/battle').LimitationResponse[]>}
     */
    getLimitations: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${id}/limitations`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getLimitations] Failed to fetch limitations for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить полный регламент баттла (правила и ограничения).
     * @param {string} id — UUID баттла
     * @returns {Promise<import('../model/battle').RegulationResponse>}
     */
    getRegulation: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${id}/regulation`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getRegulation] Failed to fetch regulation for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить правила раунда.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @returns {Promise<import('../model/battle').RoundRuleResponse[]>}
     */
    getRoundRules: async (id, roundId) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.get(`/battles/${id}/rounds/${roundId}/rules`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getRoundRules] Failed to fetch rules for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить ограничения раунда.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @returns {Promise<import('../model/battle').RoundLimitationResponse[]>}
     */
    getRoundLimitations: async (id, roundId) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.get(`/battles/${id}/rounds/${roundId}/limitations`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getRoundLimitations] Failed to fetch limitations for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавить правило к регламенту баттла.
     * @param {string} id — UUID баттла
     * @param {Object} payload — RuleRequest { title, description? }
     * @returns {Promise<import('../model/battle').RuleResponse>}
     */
    createRule: async (id, payload) => {
        validateId(id, 'Battle ID');
        if (!payload) throw new Error('Rule payload is required');
        try {
            const { data } = await api.post(`/battles/${id}/rules`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.createRule] Failed to create rule for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить правило баттла.
     * @param {string} id — UUID баттла
     * @param {string} ruleId — UUID правила
     * @param {Object} payload — RuleRequest { title, description? }
     * @returns {Promise<import('../model/battle').RuleResponse>}
     */
    updateRule: async (id, ruleId, payload) => {
        validateId(id, 'Battle ID');
        validateId(ruleId, 'Rule ID');
        if (!payload) throw new Error('Rule payload is required');
        try {
            const { data } = await api.put(`/battles/${id}/rules/${ruleId}`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.updateRule] Failed to update rule ${ruleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить правило баттла.
     * @param {string} id — UUID баттла
     * @param {string} ruleId — UUID правила
     * @returns {Promise<void>}
     */
    deleteRule: async (id, ruleId) => {
        validateId(id, 'Battle ID');
        validateId(ruleId, 'Rule ID');
        try {
            await api.delete(`/battles/${id}/rules/${ruleId}`);
        } catch (error) {
            console.error(`[battlesApi.deleteRule] Failed to delete rule ${ruleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавить ограничение к регламенту баттла.
     * @param {string} id — UUID баттла
     * @param {Object} payload — LimitationRequest { title, description?, value? }
     * @returns {Promise<import('../model/battle').LimitationResponse>}
     */
    createLimitation: async (id, payload) => {
        validateId(id, 'Battle ID');
        if (!payload) throw new Error('Limitation payload is required');
        try {
            const { data } = await api.post(`/battles/${id}/limitations`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.createLimitation] Failed to create limitation for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить ограничение баттла.
     * @param {string} id — UUID баттла
     * @param {string} limitationId — UUID ограничения
     * @param {Object} payload — LimitationRequest { title, description?, value? }
     * @returns {Promise<import('../model/battle').LimitationResponse>}
     */
    updateLimitation: async (id, limitationId, payload) => {
        validateId(id, 'Battle ID');
        validateId(limitationId, 'Limitation ID');
        if (!payload) throw new Error('Limitation payload is required');
        try {
            const { data } = await api.put(`/battles/${id}/limitations/${limitationId}`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.updateLimitation] Failed to update limitation ${limitationId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить ограничение баттла.
     * @param {string} id — UUID баттла
     * @param {string} limitationId — UUID ограничения
     * @returns {Promise<void>}
     */
    deleteLimitation: async (id, limitationId) => {
        validateId(id, 'Battle ID');
        validateId(limitationId, 'Limitation ID');
        try {
            await api.delete(`/battles/${id}/limitations/${limitationId}`);
        } catch (error) {
            console.error(`[battlesApi.deleteLimitation] Failed to delete limitation ${limitationId}:`, error.message);
            throw error;
        }
    },

    /**
     * Создать правило раунда.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {Object} payload — RoundRuleRequest { name, description, ruleType, isActive }
     * @returns {Promise<import('../model/battle').RoundRuleResponse>}
     */
    createRoundRule: async (id, roundId, payload) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Rule payload is required');
        try {
            const { data } = await api.post(`/battles/${id}/rounds/${roundId}/rules`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.createRoundRule] Failed to create rule for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить правило раунда.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} ruleId — UUID правила
     * @param {Object} payload — RoundRuleRequest
     * @returns {Promise<import('../model/battle').RoundRuleResponse>}
     */
    updateRoundRule: async (id, roundId, ruleId, payload) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(ruleId, 'Rule ID');
        if (!payload) throw new Error('Rule payload is required');
        try {
            const { data } = await api.put(`/battles/${id}/rounds/${roundId}/rules/${ruleId}`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.updateRoundRule] Failed to update rule ${ruleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить правило раунда.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} ruleId — UUID правила
     * @returns {Promise<void>}
     */
    deleteRoundRule: async (id, roundId, ruleId) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(ruleId, 'Rule ID');
        try {
            await api.delete(`/battles/${id}/rounds/${roundId}/rules/${ruleId}`);
        } catch (error) {
            console.error(`[battlesApi.deleteRoundRule] Failed to delete rule ${ruleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Создать ограничение раунда.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {Object} payload — RoundLimitationRequest
     * @returns {Promise<import('../model/battle').RoundLimitationResponse>}
     */
    createRoundLimitation: async (id, roundId, payload) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Limitation payload is required');
        try {
            const { data } = await api.post(`/battles/${id}/rounds/${roundId}/limitations`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.createRoundLimitation] Failed to create limitation for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить ограничение раунда.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} limitationId — UUID ограничения
     * @param {Object} payload — RoundLimitationRequest
     * @returns {Promise<import('../model/battle').RoundLimitationResponse>}
     */
    updateRoundLimitation: async (id, roundId, limitationId, payload) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(limitationId, 'Limitation ID');
        if (!payload) throw new Error('Limitation payload is required');
        try {
            const { data } = await api.put(`/battles/${id}/rounds/${roundId}/limitations/${limitationId}`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.updateRoundLimitation] Failed to update limitation ${limitationId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить ограничение раунда.
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {string} limitationId — UUID ограничения
     * @returns {Promise<void>}
     */
    deleteRoundLimitation: async (id, roundId, limitationId) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        validateId(limitationId, 'Limitation ID');
        try {
            await api.delete(`/battles/${id}/rounds/${roundId}/limitations/${limitationId}`);
        } catch (error) {
            console.error(`[battlesApi.deleteRoundLimitation] Failed to delete limitation ${limitationId}:`, error.message);
            throw error;
        }
    },

    /**
     * Загрузить трек в раунд (multipart/form-data).
     * Endpoint: POST /battles/{id}/rounds/{roundId}/tracks/upload
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {FormData} payload — FormData с полями: track (File), data (JSON UploadTrackRequest)
     * @param {Function} [onProgress] - Callback для отслеживания прогресса (0-100)
     * @returns {Promise<import('../model/battle').WorkResponse>} Работа с треком
     */
    uploadTrack: async (id, roundId, payload, onProgress) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Track payload is required');
        if (!(payload instanceof FormData)) {
            throw new Error('uploadTrack requires FormData with audio file');
        }

        try {
            const { data } = await api.post(`/battles/${id}/rounds/${roundId}/tracks/upload`, payload, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    if (onProgress && progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress(percentCompleted);
                    }
                }
            });
            return data;
        } catch (error) {
            if (error.response?.status === 413) {
                const errorMessage = 'Размер файла превышает допустимый лимит (макс. 30 МБ)';
                console.error(`[battlesApi.uploadTrack] File too large for round ${roundId}:`, error.message);
                throw new Error(errorMessage);
            }
            if (error.response?.status === 415) {
                const errorMessage = 'Неподдерживаемый формат файла. Допустимы: MP3, WAV, OGG, FLAC, AAC, M4A';
                console.error(`[battlesApi.uploadTrack] Unsupported format for round ${roundId}:`, error.message);
                throw new Error(errorMessage);
            }
            console.error(`[battlesApi.uploadTrack] Failed to upload track for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Создать трек с внешним URL (или текстовый трек).
     * Endpoint: POST /battles/{id}/rounds/{roundId}/tracks
     * @param {string} id — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {TrackRequest} payload — { authorId, battlePairId?, title, description, text?, duration?, format, mediaUrl?, status? }
     * @returns {Promise<import('../model/battle').WorkResponse>} Работа с треком
     * @description mediaUrl обязателен для аудио-треков, но может отсутствовать для текстовых треков (format='TEXT')
     */
    createTrackByUrl: async (id, roundId, payload) => {
        validateId(id, 'Battle ID');
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Track payload is required');
        // mediaUrl обязателен только для аудио-треков
        if (payload.format !== 'TEXT' && !payload.mediaUrl) {
            throw new Error('mediaUrl is required for audio track creation');
        }
        try {
            const { data } = await api.post(`/battles/${id}/rounds/${roundId}/tracks`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.createTrackByUrl] Failed to create track for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить регламент баттла (поля ageLimits и formats).
     * @param {string} id — UUID баттла
     * @param {Object} payload — RegulationRequest { ageLimits?, formats? }
     * @returns {Promise<import('../model/battle').RegulationResponse>}
     */
    updateRegulation: async (id, payload) => {
        validateId(id, 'Battle ID');
        if (!payload) throw new Error('Regulation payload is required');
        try {
            const { data } = await api.put(`/battles/${id}/regulation`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.updateRegulation] Failed to update regulation for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Загрузить обложку баттла.
     * @param {string} id — UUID баттла
     * @param {File} file — Файл изображения (JPEG, PNG, WebP; макс. 10 MB; 2:1 landscape)
     * @returns {Promise<import('../model/battle').BattleCoverResponse>}
     */
    uploadCover: async (id, file) => {
        validateId(id, 'Battle ID');
        if (!file) throw new Error('Cover file is required');

        const formData = new FormData();
        formData.append('cover', file);

        console.log(`[battlesApi.uploadCover] Uploading cover for battle ${id}:`, file.name, file.type, file.size);

        try {
            const { data } = await api.post(`/battles/${id}/cover`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            console.log(`[battlesApi.uploadCover] Cover uploaded successfully:`, data);
            return data;
        } catch (error) {
            console.error(`[battlesApi.uploadCover] Error:`, error.message);
            console.error(`[battlesApi.uploadCover] Response:`, error.response?.data);
            console.error(`[battlesApi.uploadCover] Status:`, error.response?.status);

            if (error.response?.status === 413) {
                const errorMessage = 'Размер файла превышает допустимый лимит (макс. 10 МБ)';
                console.error(`[battlesApi.uploadCover] File too large for battle ${id}:`, file.size, 'bytes');
                throw new Error(errorMessage);
            }
            if (error.response?.status === 400) {
                const errorMessage = error.response?.data?.message || 'Неверный формат файла. Проверьте соотношение сторон (1:1, 16:9, 9:16)';
                throw new Error(errorMessage);
            }
            if (error.response?.status === 403) {
                throw new Error('У вас нет прав для загрузки обложки');
            }
            if (error.response?.status === 404) {
                throw new Error('Баттл не найден');
            }

            console.error(`[battlesApi.uploadCover] Failed to upload cover for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить обложку баттла.
     * @param {string} id — UUID баттла
     * @returns {Promise<import('../model/battle').BattleCoverResponse>}
     */
    deleteCover: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.delete(`/battles/${id}/cover`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.deleteCover] Failed to delete cover for battle ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить информацию об обложке баттла.
     * Endpoint: GET /battles/{id}/cover
     * @param {string} id — UUID баттла
     * @returns {Promise<import('../model/battle').BattleCoverResponse>}
     * @throws {Error} 404 — У баттла нет обложки
     */
    getCover: async (id) => {
        validateId(id, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${id}/cover`);
            return data;
        } catch (error) {
            if (error.response?.status === 404) {
                console.error(`[battlesApi.getCover] No cover found for battle ${id}`);
                throw new Error('У баттла нет обложки');
            }
            console.error(`[battlesApi.getCover] Failed to fetch cover for battle ${id}:`, error.message);
            throw error;
        }
    },

    // ==================== Battle Pairs API ====================

    /**
     * Получить пары раунда.
     * Endpoint: GET /api/battle_pairs?roundId={roundId}
     * @param {string} roundId — UUID раунда
     * @param {Object} [params] — page, size, sortBy, direction
     * @returns {Promise<{content: BattlePairResponse[], pageable, totalElements, totalPages}>}
     */
    getRoundPairs: async (roundId, params = {}) => {
        validateId(roundId, 'Round ID');
        try {
            const { data } = await api.get('/battle_pairs', {
                params: { roundId, ...params }
            });
            return data;
        } catch (error) {
            console.error(`[battlesApi.getRoundPairs] Failed to fetch pairs for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить пару по ID.
     * @param {string} pairId — UUID пары
     * @returns {Promise<BattlePairResponse>}
     */
    getPairById: async (pairId) => {
        validateId(pairId, 'Pair ID');
        try {
            const { data } = await api.get(`/battle_pairs/${pairId}`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getPairById] Failed to fetch pair ${pairId}:`, error.message);
            throw error;
        }
    },

    /**
     * Создать пару участников.
     * @param {string} roundId — UUID раунда
     * @param {BattlePairRequest} payload — { firstPersonId, secondPersonId, pairNumber?, notes? }
     * @returns {Promise<BattlePairResponse>}
     */
    createPair: async (roundId, payload) => {
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Pair payload is required');
        try {
            const { data } = await api.post('/battle_pairs', { ...payload, roundId });
            return data;
        } catch (error) {
            console.error(`[battlesApi.createPair] Failed to create pair for round ${roundId}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновить пару.
     * @param {string} pairId — UUID пары
     * @param {Object} payload — { firstPersonId?, secondPersonId?, pairNumber?, notes? }
     * @returns {Promise<BattlePairResponse>}
     */
    updatePair: async (pairId, payload) => {
        validateId(pairId, 'Pair ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/battle_pairs/${pairId}`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.updatePair] Failed to update pair ${pairId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить пару.
     * @param {string} pairId — UUID пары
     * @returns {Promise<void>}
     */
    deletePair: async (pairId) => {
        validateId(pairId, 'Pair ID');
        try {
            await api.delete(`/battle_pairs/${pairId}`);
        } catch (error) {
            console.error(`[battlesApi.deletePair] Failed to delete pair ${pairId}:`, error.message);
            throw error;
        }
    },

    /**
     * Объявить победителя пары.
     * @param {string} pairId — UUID пары
     * @param {string} winnerId — UUID победителя (должен быть одним из участников пары)
     * @returns {Promise<BattlePairResponse>}
     */
    setPairWinner: async (pairId, winnerId) => {
        validateId(pairId, 'Pair ID');
        validateId(winnerId, 'Winner ID');
        try {
            const { data } = await api.post(`/battle_pairs/${pairId}/winner`, { winnerId });
            return data;
        } catch (error) {
            console.error(`[battlesApi.setPairWinner] Failed to set winner for pair ${pairId}:`, error.message);
            throw error;
        }
    },

    /**
     * Изменить статус пары.
     * @param {string} pairId — UUID пары
     * @param {'PENDING'|'ACTIVE'|'FINISHED'|'DISQUALIFIED'} status — Новый статус
     * @returns {Promise<BattlePairResponse>}
     */
    updatePairStatus: async (pairId, status) => {
        validateId(pairId, 'Pair ID');
        if (!status) throw new Error('Status is required');
        try {
            const { data } = await api.patch(`/battle_pairs/${pairId}/status`, { status });
            return data;
        } catch (error) {
            console.error(`[battlesApi.updatePairStatus] Failed to update pair ${pairId} status:`, error.message);
            throw error;
        }
    },

    /**
     * Активировать пару.
     * @param {string} pairId — UUID пары
     * @returns {Promise<BattlePairResponse>}
     */
    activatePair: async (pairId) => {
        validateId(pairId, 'Pair ID');
        try {
            const { data } = await api.post(`/battle_pairs/${pairId}/activate`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.activatePair] Failed to activate pair ${pairId}:`, error.message);
            throw error;
        }
    },

    /**
     * Завершить пару с победителем.
     * @param {string} pairId — UUID пары
     * @param {string} winnerId — UUID победителя
     * @returns {Promise<BattlePairResponse>}
     */
    finishPair: async (pairId, winnerId) => {
        validateId(pairId, 'Pair ID');
        validateId(winnerId, 'Winner ID');
        try {
            const { data } = await api.post(`/battle_pairs/${pairId}/finish`, { winnerId });
            return data;
        } catch (error) {
            console.error(`[battlesApi.finishPair] Failed to finish pair ${pairId}:`, error.message);
            throw error;
        }
    },

    /**
     * Дисквалифицировать пару.
     * @param {string} pairId — UUID пары
     * @returns {Promise<BattlePairResponse>}
     */
    disqualifyPair: async (pairId) => {
        validateId(pairId, 'Pair ID');
        try {
            const { data } = await api.post(`/battle_pairs/${pairId}/disqualify`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.disqualifyPair] Failed to disqualify pair ${pairId}:`, error.message);
            throw error;
        }
    },

    /**
     * Сгенерировать пары для раунда (жеребьёвка).
     * @param {string} roundId — UUID раунда
     * @param {PairDrawRequest} payload — { drawType: 'RANDOM'|'SEEDED', shuffle?: boolean }
     * @returns {Promise<PairDrawResponse>}
     */
    generatePairs: async (roundId, payload) => {
        validateId(roundId, 'Round ID');
        if (!payload) throw new Error('Draw payload is required');
        try {
            const { data } = await api.post(`/battle_pairs/round/${roundId}/generate`, payload);
            return data;
        } catch (error) {
            console.error(`[battlesApi.generatePairs] Failed to generate pairs for round ${roundId}:`, error.message);
            throw error;
        }
    },

    // ==================== Battle Chat API ====================

    /**
     * Получить чат для зрителей (Live Chat).
     * Endpoint: GET /api/battles/{battleId}/chats/live
     * @param {string} battleId — UUID баттла
     * @returns {Promise<{id: string, type: 'BATTLE_LIVE', name: string, description: string, battleId: string, ownerId: string, participantCount: number, messageCount: number, lastMessageAt: string, createdAt: string}>}
     */
    getLiveChat: async (battleId) => {
        validateId(battleId, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${battleId}/chats/live`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getLiveChat] Failed to fetch live chat for battle ${battleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить чат участников (Participants Chat).
     * Endpoint: GET /api/battles/{battleId}/chats/participants
     * @param {string} battleId — UUID баттла
     * @returns {Promise<{id: string, type: 'BATTLE_PARTICIPANTS', name: string, description: string, battleId: string, ownerId: string, participants: Array<{profileId: string, name: string, role: string}>, participantCount: number, messageCount: number, lastMessageAt: string, createdAt: string}>}
     */
    getParticipantsChat: async (battleId) => {
        validateId(battleId, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${battleId}/chats/participants`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getParticipantsChat] Failed to fetch participants chat for battle ${battleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить все чаты баттла.
     * Endpoint: GET /api/battles/{battleId}/chats
     * @param {string} battleId — UUID баттла
     * @returns {Promise<Array<{id: string, type: string, name: string, participantCount: number, messageCount: number}>>}
     */
    getChats: async (battleId) => {
        validateId(battleId, 'Battle ID');
        try {
            const { data } = await api.get(`/battles/${battleId}/chats`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.getChats] Failed to fetch chats for battle ${battleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавить участника в чат участников.
     * Endpoint: POST /api/battles/{battleId}/chats/participants/add
     * @param {string} battleId — UUID баттла
     * @param {string} profileId — UUID профиля участника
     * @param {string} role — Роль участника (OPPONENT, REFEREE, VIEWER)
     * @returns {Promise<{id: string, type: 'BATTLE_PARTICIPANTS', name: string, participants: Array<{profileId: string, name: string, role: string}>, participantCount: number}>}
     */
    addParticipantToChat: async (battleId, profileId, role = 'PARTICIPANT') => {
        validateId(battleId, 'Battle ID');
        validateId(profileId, 'Profile ID');
        try {
            const { data } = await api.post(
                `/battles/${battleId}/chats/participants/add`,
                null,
                { params: { profileId, role } }
            );
            return data;
        } catch (error) {
            console.error(`[battlesApi.addParticipantToChat] Failed to add participant ${profileId} to chat ${battleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить участника из чата участников.
     * Endpoint: POST /api/battles/{battleId}/chats/participants/remove
     * @param {string} battleId — UUID баттла
     * @param {string} profileId — UUID профиля участника
     * @returns {Promise<{id: string, type: 'BATTLE_PARTICIPANTS', name: string, participants: Array<{profileId: string, name: string, role: string}>, participantCount: number}>}
     */
    removeParticipantFromChat: async (battleId, profileId) => {
        validateId(battleId, 'Battle ID');
        validateId(profileId, 'Profile ID');
        try {
            const { data } = await api.post(
                `/battles/${battleId}/chats/participants/remove`,
                null,
                { params: { profileId } }
            );
            return data;
        } catch (error) {
            console.error(`[battlesApi.removeParticipantFromChat] Failed to remove participant ${profileId} from chat ${battleId}:`, error.message);
            throw error;
        }
    },

    // ==================== Work Upload API ====================

    /**
     * Загрузить работу (аудио) для раунда баттла.
     * Endpoint: POST /api/battles/{id}/rounds/{roundId}/tracks/upload
     * @param {string} battleId — UUID баттла
     * @param {string} roundId — UUID раунда
     * @param {FormData} formData - FormData с файлом и метаданными
     * @param {Function} [onProgress] - Callback для отслеживания прогресса
     * @returns {Promise<import('../model/battle').WorkResponse>}
     * @deprecated Используйте uploadTrack
     */
    uploadWork: async (battleId, roundId, formData, onProgress) => {
        return this.uploadTrack(battleId, roundId, formData, onProgress);
    },

    // ==================== Battle Invite API ====================
    // Базовый путь: /api/battles/invites
    // inviteId — это ID сущности BattlePerson (inviteId из InvitationAttachment).
    // @see api_docs_v2/battle/battle_invite_api.md

    /**
     * Принять приглашение в баттл. Статус BattlePerson → APPROVED.
     * Endpoint: POST /api/battles/invites/{inviteId}/accept
     * @param {string} inviteId — ID BattlePerson (inviteId из InvitationAttachment)
     * @returns {Promise<Object>} BattleInviteResponse
     */
    acceptInvite: async (inviteId) => {
        validateId(inviteId, 'Invite ID');
        try {
            const { data } = await api.post(`/battles/invites/${inviteId}/accept`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.acceptInvite] Failed to accept battle invite ${inviteId}:`, error.message);
            throw error;
        }
    },

    /**
     * Отклонить приглашение в баттл. Статус BattlePerson → REJECTED.
     * Endpoint: PATCH /api/battles/invites/{inviteId}/decline
     * @param {string} inviteId — ID BattlePerson (inviteId из InvitationAttachment)
     * @returns {Promise<Object>} BattleInviteResponse
     */
    declineInvite: async (inviteId) => {
        validateId(inviteId, 'Invite ID');
        try {
            const { data } = await api.patch(`/battles/invites/${inviteId}/decline`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.declineInvite] Failed to decline battle invite ${inviteId}:`, error.message);
            throw error;
        }
    },

    /**
     * Отозвать приглашение организатором. Статус BattlePerson → WITHDRAWN.
     * Endpoint: DELETE /api/battles/invites/{inviteId}
     * @param {string} inviteId — ID BattlePerson (inviteId из InvitationAttachment)
     * @returns {Promise<Object>} BattleInviteResponse
     */
    revokeInvite: async (inviteId) => {
        validateId(inviteId, 'Invite ID');
        try {
            const { data } = await api.delete(`/battles/invites/${inviteId}`);
            return data;
        } catch (error) {
            console.error(`[battlesApi.revokeInvite] Failed to revoke battle invite ${inviteId}:`, error.message);
            throw error;
        }
    }
};
